import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FaBell } from 'react-icons/fa';
import { isPushSupported, subscribeToPush } from '../../utils/pushNotifications';
import { showToast } from '../../utils/toast';
import './PushNotificationPrompt.scss';

const DISMISS_KEY = 'mivent_push_prompt_dismissed';
const SESSION_KEY = 'mivent_push_prompt_shown_session';
const INSTALL_OVERLAY_SELECTOR = '.install-popup-overlay';
const INSTALL_POLL_MS = 500;
const INSTALL_MAX_WAIT_MS = 15000;

const PushNotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [busy, setBusy] = useState(false);
  const { isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!isAuthenticated || !isPushSupported()) return undefined;

    // Never nag users who opted out or already decided.
    if (
      localStorage.getItem(DISMISS_KEY) === 'true' ||
      sessionStorage.getItem(SESSION_KEY) === 'true' ||
      typeof Notification === 'undefined'
    ) {
      return undefined;
    }

    // Only prompt when the browser permission is still undecided. Granted
    // users are silently re-subscribed by syncPushOnLogin(); denied users
    // cannot be re-prompted from the page.
    if (Notification.permission !== 'default') return undefined;

    let cancelled = false;
    let poll = null;

    const showNow = () => {
      if (cancelled) return;
      setShowPrompt(true);
      sessionStorage.setItem(SESSION_KEY, 'true');
    };

    const isInstallVisible = () => {
      try {
        return !!document.querySelector(INSTALL_OVERLAY_SELECTOR);
      } catch {
        return false;
      }
    };

    // Show instantly right after login — only yield while the install popup
    // is actually on screen so the two prompts never overlap.
    if (!isInstallVisible()) {
      showNow();
      return () => {
        cancelled = true;
      };
    }

    const startedAt = Date.now();
    poll = setInterval(() => {
      if (cancelled) return;
      if (!isInstallVisible() || Date.now() - startedAt >= INSTALL_MAX_WAIT_MS) {
        clearInterval(poll);
        poll = null;
        showNow();
      }
    }, INSTALL_POLL_MS);

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
    };
  }, [isAuthenticated]);

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);

    const result = await subscribeToPush();
    if (result.subscribed) {
      showToast('Notifications enabled — you’ll get alerts for new records & reminders even when the app is closed', { type: 'success' });
    } else if (result.denied || result.granted === false) {
      showToast('Notification permission is blocked. Allow notifications in your browser/device settings.', { type: 'error', duration: 6000 });
    } else {
      showToast('Could not enable push notifications on this device.', { type: 'error' });
    }

    setBusy(false);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    if (dontAskAgain) {
      localStorage.setItem(DISMISS_KEY, 'true');
    }
    sessionStorage.setItem(SESSION_KEY, 'true');
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div className="push-prompt" role="dialog" aria-label="Enable notifications">
      <div className="push-prompt__icon">
        <FaBell />
      </div>
      <div className="push-prompt__body">
        <h4>Turn on push &amp; announcement alerts</h4>
        <p>
          Allow notifications to get instant push alerts for new records, reminders and announcements — even when MIVENT is closed. Your browser will ask for permission when you tap Enable.
        </p>
        <label className="push-prompt__checkbox">
          <input
            type="checkbox"
            checked={dontAskAgain}
            onChange={(e) => setDontAskAgain(e.target.checked)}
          />
          Don&apos;t ask again
        </label>
      </div>
      <div className="push-prompt__actions">
        <button
          type="button"
          className="push-prompt__btn push-prompt__btn--later"
          onClick={handleDismiss}
        >
          Not now
        </button>
        <button
          type="button"
          className="push-prompt__btn push-prompt__btn--enable"
          onClick={handleEnable}
          disabled={busy}
        >
          {busy ? 'Enabling…' : 'Enable'}
        </button>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
